import { LeagueId, Match } from '../types';
import { KNOCKOUT_ROUNDS, BRACKET_SLOT_DISPLAY_ORDER } from '../constants/mundialBracket';

export function startOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function toDate(value: unknown): Date {
  if (value instanceof Date) return value;
  if (value && typeof value === 'object' && 'toDate' in value) {
    return (value as { toDate: () => Date }).toDate();
  }
  return new Date(value as string | number);
}

/** true si aún se puede pronosticar (antes del pitido inicial y sin resultado oficial). */
export function isMatchPredictionOpen(match: Match, now: Date = new Date()): boolean {
  if (match.score_a !== null && match.score_b !== null) return false;
  return now.getTime() < toDate(match.date).getTime();
}

/** Usuarios que pueden capturar después del pitido inicial (hasta que haya resultado oficial). */
const PREDICTION_KICKOFF_BYPASS_USER_IDS = new Set([
  'v2aquJjaOeTIU9ADuk93cEwHV8O2',
]);

export function hasPredictionKickoffBypass(userId?: string | null): boolean {
  return !!userId && PREDICTION_KICKOFF_BYPASS_USER_IDS.has(userId);
}

/** Evalúa si un usuario puede capturar pronóstico para un partido. */
export function canUserPredictMatch(
  userId: string | undefined | null,
  match: Match,
  now: Date = new Date()
): boolean {
  if (hasPredictionKickoffBypass(userId)) {
    return match.score_a === null || match.score_b === null;
  }
  return isMatchPredictionOpen(match, now);
}

/** Jornada 1–3 dentro de cada grupo: 2 partidos por jornada (orden cronológico). */
export function getMundialGroupMatchday(match: Match, groupMatches: Match[]): number {
  if (match.jornada != null && match.jornada >= 1 && match.jornada <= 3) {
    return match.jornada;
  }

  if (!match.group) return 1;

  const inGroup = groupMatches
    .filter((m) => m.group?.toUpperCase() === match.group?.toUpperCase())
    .sort((a, b) => {
      const dateCmp = toDate(a.date).getTime() - toDate(b.date).getTime();
      if (dateCmp !== 0) return dateCmp;
      return a.id.localeCompare(b.id);
    });

  const index = inGroup.findIndex((m) => m.id === match.id);
  if (index < 0) return 1;

  return Math.floor(index / 2) + 1;
}

/**
 * Jornada global de grupos: la más alta cuya primera fecha ya llegó (<= hoy).
 * Antes del primer partido → jornada 1.
 */
export function getCurrentMundialGroupJornada(
  groupMatches: Match[],
  today: Date = new Date()
): number {
  if (groupMatches.length === 0) return 1;

  const todayMs = startOfDay(today).getTime();
  const jornadaStartDates = new Map<number, number>();

  groupMatches.forEach((m) => {
    const j = getMundialGroupMatchday(m, groupMatches);
    const dayMs = startOfDay(toDate(m.date)).getTime();
    const current = jornadaStartDates.get(j);
    if (current === undefined || dayMs < current) {
      jornadaStartDates.set(j, dayMs);
    }
  });

  let current = 1;
  const jornadas = Array.from(jornadaStartDates.keys()).sort((a, b) => a - b);
  jornadas.forEach((j) => {
    const start = jornadaStartDates.get(j)!;
    if (todayMs >= start) current = j;
  });

  return current;
}

export function parseLigaMxJornadaNumber(phase: string): number | null {
  const match = phase.match(/jornada\s*(\d+)/i);
  return match ? Number(match[1]) : null;
}

export function getCurrentLigaMxJornada(
  matches: Match[],
  today: Date = new Date()
): number {
  const jornadaMatches = matches.filter((m) => parseLigaMxJornadaNumber(m.phase) !== null);
  if (jornadaMatches.length === 0) return 1;

  const todayMs = startOfDay(today).getTime();
  const startByJornada = new Map<number, number>();

  jornadaMatches.forEach((m) => {
    const j = parseLigaMxJornadaNumber(m.phase)!;
    const dayMs = startOfDay(toDate(m.date)).getTime();
    const cur = startByJornada.get(j);
    if (cur === undefined || dayMs < cur) startByJornada.set(j, dayMs);
  });

  let current = 1;
  Array.from(startByJornada.keys())
    .sort((a, b) => a - b)
    .forEach((j) => {
      if (todayMs >= startByJornada.get(j)!) current = j;
    });

  return current;
}

export function getCurrentJornada(matches: Match[], league: LeagueId): number {
  if (league === 'mundial_fifa') {
    const groupMatches = matches.filter((m) => m.match_type === 'group');
    return getCurrentMundialGroupJornada(groupMatches);
  }
  return getCurrentLigaMxJornada(matches);
}

export function filterMatchesForCurrentJornada(
  matches: Match[],
  league: LeagueId
): Match[] {
  const current = getCurrentJornada(matches, league);

  if (league === 'mundial_fifa') {
    const groupMatches = matches.filter((m) => m.match_type === 'group');
    return groupMatches.filter(
      (m) => getMundialGroupMatchday(m, groupMatches) === current
    );
  }

  return matches.filter((m) => parseLigaMxJornadaNumber(m.phase) === current);
}

export function sortMatchesForCapture(matches: Match[], league: LeagueId): Match[] {
  const sorted = [...matches].sort(
    (a, b) => toDate(a.date).getTime() - toDate(b.date).getTime()
  );

  if (league === 'mundial_fifa') {
    return sorted.sort((a, b) => {
      const ga = a.group?.toUpperCase() ?? '';
      const gb = b.group?.toUpperCase() ?? '';
      if (ga !== gb) return ga.localeCompare(gb);
      return toDate(a.date).getTime() - toDate(b.date).getTime();
    });
  }

  return sorted.sort((a, b) => {
    const ja = parseLigaMxJornadaNumber(a.phase) ?? 0;
    const jb = parseLigaMxJornadaNumber(b.phase) ?? 0;
    if (ja !== jb) return ja - jb;
    return toDate(a.date).getTime() - toDate(b.date).getTime();
  });
}

export function getJornadaLabel(league: LeagueId, jornada: number): string {
  if (league === 'mundial_fifa') {
    return `Jornada ${jornada} — Fase de grupos`;
  }
  return `Jornada ${jornada}`;
}

export const MUNDIAL_GROUP_JORNADAS = [1, 2, 3] as const;

export function isMundialGroupMatch(m: Match): boolean {
  return (
    m.league === 'mundial_fifa' &&
    (m.match_type === 'group' || (!m.match_type && !!m.group))
  );
}

export function getMundialGroupMatches(matches: Match[]): Match[] {
  return matches.filter(isMundialGroupMatch);
}

export function isMundialKnockoutMatch(m: Match): boolean {
  return m.league === 'mundial_fifa' && !isMundialGroupMatch(m);
}

export function getMundialKnockoutMatches(matches: Match[]): Match[] {
  return matches.filter(isMundialKnockoutMatch);
}

const KNOCKOUT_PHASE_ORDER = new Map(
  KNOCKOUT_ROUNDS.map((round, index) => [round.phaseLabel, index])
);

export function sortKnockoutPhases(phases: string[]): string[] {
  return [...phases].sort(
    (a, b) =>
      (KNOCKOUT_PHASE_ORDER.get(a) ?? 99) - (KNOCKOUT_PHASE_ORDER.get(b) ?? 99) ||
      a.localeCompare(b)
  );
}

export function getKnockoutPhaseSortIndex(match: Match): number {
  const byType = KNOCKOUT_ROUNDS.findIndex((r) => r.matchType === match.match_type);
  if (byType >= 0) return byType;
  return KNOCKOUT_PHASE_ORDER.get(match.phase) ?? 99;
}

function bracketSlotSortKey(slot?: string | null): number {
  if (!slot) return 9999;
  const index = BRACKET_SLOT_DISPLAY_ORDER.indexOf(slot);
  return index >= 0 ? index : 9999;
}

/** Orden numérico R32-01…R32-16 (calendario / captura por fecha). */
function bracketSlotNumericKey(slot?: string | null): number {
  if (!slot) return 9999;
  const m = slot.match(/R32-(\d+)/);
  if (m) return parseInt(m[1], 10);
  const m16 = slot.match(/R16-(\d+)/);
  if (m16) return 100 + parseInt(m16[1], 10);
  const mQf = slot.match(/QF-(\d+)/);
  if (mQf) return 200 + parseInt(mQf[1], 10);
  if (slot === 'SF-01') return 301;
  if (slot === 'SF-02') return 302;
  if (slot === '3RD' || slot === 'THIRD' || slot === 'TP-01') return 350;
  if (slot === 'FINAL') return 400;
  return 9999;
}

/** Captura de quiniela: cronológico por fecha, luego slot numérico. */
export function sortMundialKnockoutMatchesForCapture(matches: Match[]): Match[] {
  return [...matches].sort((a, b) => {
    const phaseCmp = getKnockoutPhaseSortIndex(a) - getKnockoutPhaseSortIndex(b);
    if (phaseCmp !== 0) return phaseCmp;
    const dateCmp = toDate(a.date).getTime() - toDate(b.date).getTime();
    if (dateCmp !== 0) return dateCmp;
    return bracketSlotNumericKey(a.bracket_slot) - bracketSlotNumericKey(b.bracket_slot);
  });
}

/** Bracket visual / admin: orden de la llave en pantalla. */
export function sortMundialKnockoutMatches(matches: Match[]): Match[] {
  return [...matches].sort((a, b) => {
    const phaseCmp = getKnockoutPhaseSortIndex(a) - getKnockoutPhaseSortIndex(b);
    if (phaseCmp !== 0) return phaseCmp;
    const slotCmp = bracketSlotSortKey(a.bracket_slot) - bracketSlotSortKey(b.bracket_slot);
    if (slotCmp !== 0) return slotCmp;
    return toDate(a.date).getTime() - toDate(b.date).getTime();
  });
}

export type MundialKnockoutCaptureBlock = {
  phase: string;
  label: string;
  matches: Match[];
};

export function groupMundialKnockoutByPhase(matches: Match[]): MundialKnockoutCaptureBlock[] {
  const byPhase = new Map<string, Match[]>();
  matches.forEach((m) => {
    const phase = getKnockoutPhaseLabel(m);
    if (!byPhase.has(phase)) byPhase.set(phase, []);
    byPhase.get(phase)!.push(m);
  });

  return sortKnockoutPhases(Array.from(byPhase.keys())).map((phase) => ({
    phase,
    label: phase,
    matches: sortMundialKnockoutMatchesForCapture(byPhase.get(phase) ?? []),
  }));
}

export function getActiveMundialKnockoutPhases(knockoutMatches: Match[]): string[] {
  const phases = new Set(
    knockoutMatches.filter((m) => m.activo).map((m) => getKnockoutPhaseLabel(m))
  );
  return sortKnockoutPhases(Array.from(phases));
}

export function formatMundialCaptureLabel(
  groupJornadas: number[],
  knockoutPhases: string[]
): string {
  const parts: string[] = [];
  if (groupJornadas.length === 1) {
    parts.push(getJornadaLabel('mundial_fifa', groupJornadas[0]));
  } else if (groupJornadas.length > 1) {
    parts.push(formatActiveMundialJornadasLabel(groupJornadas));
  }
  if (knockoutPhases.length === 1) {
    parts.push(knockoutPhases[0]);
  } else if (knockoutPhases.length > 1) {
    parts.push(knockoutPhases.join(' · '));
  }
  return parts.join(' · ');
}

export function getKnockoutPhaseLabel(match: Match): string {
  const fromType = KNOCKOUT_ROUNDS.find((r) => r.matchType === match.match_type)?.phaseLabel;
  if (fromType) return fromType;
  if (match.match_type === 'third_place' || match.phase === 'third_place') {
    return 'Tercer lugar';
  }
  return match.phase || 'Eliminatorias';
}

export function getMatchIdsForMundialKnockoutPhase(
  allMatches: Match[],
  phase: string
): string[] {
  const phaseNorm = phase.trim().toLowerCase();
  const isThirdPlace =
    phaseNorm === 'tercer lugar' ||
    phaseNorm === 'third_place' ||
    phaseNorm === '3er lugar';

  return getMundialKnockoutMatches(allMatches)
    .filter((m) => {
      if (isThirdPlace) return m.match_type === 'third_place';
      return getKnockoutPhaseLabel(m) === phase || m.phase === phase;
    })
    .map((m) => m.id);
}

export function filterMatchesByMundialJornada(
  matches: Match[],
  jornada: number,
  allGroupMatches?: Match[]
): Match[] {
  const pool = allGroupMatches ?? getMundialGroupMatches(matches);
  return getMundialGroupMatches(matches).filter(
    (m) => getMundialGroupMatchday(m, pool) === jornada
  );
}

/** Jornadas de grupos con al menos un partido activo (orden ascendente). */
export function getActiveMundialJornadas(groupMatches: Match[]): number[] {
  const active = groupMatches.filter((m) => m.activo);
  if (active.length === 0) return [];
  const jornadas = new Set(
    active.map((m) => getMundialGroupMatchday(m, groupMatches))
  );
  return Array.from(jornadas).sort((a, b) => a - b);
}

/** Jornada de grupos con al menos un partido activo (la más baja si hay varias). */
export function getActiveMundialJornada(groupMatches: Match[]): number | null {
  const jornadas = getActiveMundialJornadas(groupMatches);
  return jornadas.length > 0 ? jornadas[0] : null;
}

export function formatActiveMundialJornadasLabel(jornadas: number[]): string {
  if (jornadas.length === 0) return '';
  if (jornadas.length === 1) return getJornadaLabel('mundial_fifa', jornadas[0]);
  if (jornadas.length === 2) {
    return `Jornadas ${jornadas[0]} y ${jornadas[1]} — Fase de grupos`;
  }
  const allButLast = jornadas.slice(0, -1).join(', ');
  return `Jornadas ${allButLast} y ${jornadas[jornadas.length - 1]} — Fase de grupos`;
}

/** Partidos activos visibles en admin/rankings: todas las jornadas de grupos activas + eliminatorias activas. */
export function filterActiveMundialMatches(allMatches: Match[]): Match[] {
  const groupPool = getMundialGroupMatches(allMatches);
  const knockout = allMatches.filter(
    (m) => m.league === 'mundial_fifa' && m.activo && !isMundialGroupMatch(m)
  );
  const groupActive = groupPool.filter((m) => m.activo);
  return [...groupActive, ...knockout];
}

export function getMatchIdsForMundialJornada(
  allMatches: Match[],
  jornada: number
): string[] {
  const groupPool = getMundialGroupMatches(allMatches);
  return filterMatchesByMundialJornada(groupPool, jornada, groupPool).map((m) => m.id);
}

export function applyDefaultMundialActivation<T extends Match>(matches: T[]): T[] {
  const groupPool = getMundialGroupMatches(matches);
  return matches.map((m) => {
    if (!isMundialGroupMatch(m)) {
      return { ...m, activo: false };
    }
    const jornada = getMundialGroupMatchday(m, groupPool);
    return { ...m, jornada, activo: jornada === 1 };
  });
}

function groupMundialMatchesByGroup(
  matches: Match[],
  keyPrefix = ''
): { key: string; label: string; matches: Match[] }[] {
  const byGroup = new Map<string, Match[]>();
  matches.forEach((m) => {
    const g = m.group?.toUpperCase() || '—';
    if (!byGroup.has(g)) byGroup.set(g, []);
    byGroup.get(g)!.push(m);
  });
  return Array.from(byGroup.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([group, groupMatches]) => ({
      key: `${keyPrefix}${group}`,
      label: `Grupo ${group}`,
      matches: groupMatches.sort(
        (a, b) => toDate(a.date).getTime() - toDate(b.date).getTime()
      ),
    }));
}

export type MundialJornadaCaptureBlock = {
  jornada: number;
  label: string;
  groups: { key: string; label: string; matches: Match[] }[];
};

export type MundialCaptureSection = {
  key: string;
  label: string;
  kind: 'groups' | 'knockout';
  jornadaBlocks?: MundialJornadaCaptureBlock[];
  knockoutBlocks?: MundialKnockoutCaptureBlock[];
  groups?: { key: string; label: string; matches: Match[] }[];
};

/** Agrupa partidos del mundial por jornada y, dentro de cada una, por grupo. */
export function groupMundialMatchesByJornada(
  matches: Match[],
  allGroupMatches?: Match[]
): MundialJornadaCaptureBlock[] {
  const pool = allGroupMatches ?? matches;
  const jornadaOf = (m: Match) => getMundialGroupMatchday(m, pool);
  const jornadas = Array.from(new Set(matches.map(jornadaOf))).sort((a, b) => a - b);

  return jornadas.map((jornada) => ({
    jornada,
    label: getJornadaLabel('mundial_fifa', jornada),
    groups: groupMundialMatchesByGroup(
      matches.filter((m) => jornadaOf(m) === jornada),
      `j${jornada}-`
    ),
  }));
}

/** Secciones de captura: fase de grupos (por jornada) + eliminatorias (por ronda). */
export function buildMundialCaptureSections(matches: Match[]): MundialCaptureSection[] {
  const groupMatches = getMundialGroupMatches(matches);
  const knockoutMatches = getMundialKnockoutMatches(matches);
  const sections: MundialCaptureSection[] = [];

  if (groupMatches.length > 0) {
    const jornadaBlocks = groupMundialMatchesByJornada(groupMatches, groupMatches);
    sections.push({
      key: 'groups',
      label: 'Fase de grupos',
      kind: 'groups',
      jornadaBlocks,
      groups:
        jornadaBlocks.length === 1
          ? jornadaBlocks[0].groups
          : undefined,
    });
  }

  if (knockoutMatches.length > 0) {
    const knockoutBlocks = groupMundialKnockoutByPhase(knockoutMatches);
    sections.push({
      key: 'knockout',
      label: 'Eliminatorias',
      kind: 'knockout',
      knockoutBlocks,
    });
  }

  return sections;
}

/** Agrupa partidos para la UI de captura */
export function groupMatchesForDisplay(
  matches: Match[],
  league: LeagueId,
  allGroupMatches?: Match[]
): { key: string; label: string; matches: Match[] }[] {
  if (league === 'mundial_fifa') {
    return groupMundialMatchesByGroup(matches);
  }

  const byPhase = new Map<string, Match[]>();
  matches.forEach((m) => {
    if (!byPhase.has(m.phase)) byPhase.set(m.phase, []);
    byPhase.get(m.phase)!.push(m);
  });
  return Array.from(byPhase.entries())
    .sort(([a], [b]) => {
      const na = parseLigaMxJornadaNumber(a) ?? 0;
      const nb = parseLigaMxJornadaNumber(b) ?? 0;
      return na - nb || a.localeCompare(b);
    })
    .map(([phase, phaseMatches]) => ({
      key: phase,
      label: phase,
      matches: phaseMatches.sort(
        (a, b) => toDate(a.date).getTime() - toDate(b.date).getTime()
      ),
    }));
}
